import type { JSX } from "react"

import { INSERT_TABLE_COMMAND } from "@lexical/table"
import { EditorThemeClasses, Klass, LexicalEditor, LexicalNode } from "lexical"
import { createContext, useEffect, useState } from "react"
import { Button, Input } from "antd"
import { useIntl } from "react-intl"

export type CellContextShape = {
  cellEditorConfig: null | CellEditorConfig
  cellEditorPlugins: null | JSX.Element | Array<JSX.Element>
  set: (
    cellEditorConfig: null | CellEditorConfig,
    cellEditorPlugins: null | JSX.Element | Array<JSX.Element>
  ) => void
}

export type CellEditorConfig = Readonly<{
  namespace: string
  nodes?: ReadonlyArray<Klass<LexicalNode>>
  onError: (error: Error, editor: LexicalEditor) => void
  readOnly?: boolean
  theme?: EditorThemeClasses
}>

export function InsertTableDialog({
  activeEditor,
  onClose,
}: {
  activeEditor: LexicalEditor
  onClose: () => void
}): JSX.Element {
  const [rows, setRows] = useState("5")
  const [columns, setColumns] = useState("5")
  const [isDisabled, setIsDisabled] = useState(true)

  const intl = useIntl()

  useEffect(() => {
    const row = Number(rows)
    const column = Number(columns)
    if (row && row > 0 && row <= 500 && column && column > 0 && column <= 50) {
      setIsDisabled(false)
    } else {
      setIsDisabled(true)
    }
  }, [rows, columns])

  const onClick = () => {
    activeEditor.dispatchCommand(INSERT_TABLE_COMMAND, {
      columns,
      rows,
      includeHeaders: false,
    })

    onClose()
  }

  return (
    <div className="InsertTableDialog">
      <label>
        <span>{intl.formatMessage({ id: "common.rows" })}</span>
        <Input
          placeholder={intl.formatMessage({ id: "common.tablePlaceholderRows" })}
          onChange={(e: any) => setRows(e.target.value)}
          value={rows}
          data-test-id="table-modal-rows"
          type="number"
        />
      </label>
      <label>
        <span>{intl.formatMessage({ id: "common.columns" })}</span>
        <Input
          placeholder={intl.formatMessage({ id: "common.tablePlaceholderColumns" })}
          onChange={(e: any) => setColumns(e.target.value)}
          value={columns}
          data-test-id="table-modal-columns"
          type="number"
        />
      </label>
      <Button disabled={isDisabled} onClick={onClick}>
        {intl.formatMessage({ id: "common.confirm" })}
      </Button>
    </div>
  )
}

export const CellContext = createContext<CellContextShape>({
  cellEditorConfig: null,
  cellEditorPlugins: null,
  set: () => {
    // Empty
  },
})
